import { useState, useEffect, useCallback } from 'react';
import { getEventsFromSupabase, deleteAllEventsFromSupabase } from '../../utils/analytics';
import type { TrackingEvent } from '../../utils/analytics';
import StatsOverview from './components/StatsOverview';
import DailyChart from './components/DailyChart';
import EventsList from './components/EventsList';
import LoginGate from './components/LoginGate';
import TopSections from './components/TopSections';

export default function AdminPage() {
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [refreshed, setRefreshed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getEventsFromSupabase();
    setEvents(data);
    setLastRefresh(new Date());
    setLoading(false);
    setRefreshed(true);
    setTimeout(() => setRefreshed(false), 1800);
  }, []);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = 'Dashboard Conversioni – Punto Cassonetti';
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleClear = async () => {
    await deleteAllEventsFromSupabase();
    setEvents([]);
    setShowClearConfirm(false);
  };

  const today = new Date().toISOString().slice(0, 10);
  const todayEvents = events.filter((e) => e.date === today).length;

  return (
    <LoginGate>
      <div className="min-h-screen bg-[#F6F6F6]">
        {/* Header */}
        <div className="bg-white border-b border-[#E5E5E5]">
          <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img
                src="https://static.readdy.ai/image/fab5168e291508d3586d4b84ed71e8b7/81e3031b4bcc562bb081a6ae4aa9af2c.png"
                alt="Punto Cassonetti"
                className="h-8 w-auto object-contain"
              />
              <div>
                <h1 className="text-sm font-black text-[#000000]" style={{ fontFamily: "'Raleway', sans-serif" }}>
                  Dashboard Conversioni
                </h1>
                <p className="text-[10px] text-[#A5A5A5]">
                  Aggiornato alle {lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  {' · '}
                  <span className="text-[#7FCDCD] font-bold">{todayEvents} eventi oggi</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={load}
                disabled={loading || refreshed}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  refreshed
                    ? 'bg-[#7FCDCD]/15 text-[#7FCDCD]'
                    : 'bg-[#F6F6F6] text-[#555555] hover:bg-[#E5E5E5]'
                }`}
              >
                <i className={`text-sm ${refreshed ? 'ri-check-line' : loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}`} />
                {refreshed ? 'Aggiornato!' : loading ? 'Caricamento...' : 'Aggiorna'}
              </button>
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line text-sm" />
                Svuota dati
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 flex flex-col gap-6">

          {/* Supabase connected notice */}
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <i className="ri-database-2-line text-emerald-500 text-base mt-0.5 flex-shrink-0" />
            <p className="text-xs text-emerald-700 leading-relaxed">
              <strong>Dati in tempo reale:</strong> ogni click di ogni visitatore viene salvato nel database e visibile qui, da qualsiasi dispositivo.
            </p>
          </div>

          {/* Loading skeleton */}
          {loading && events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <i className="ri-loader-4-line text-3xl text-[#7FCDCD] animate-spin" />
              <p className="text-sm text-[#A5A5A5]">Caricamento dati...</p>
            </div>
          ) : (
            <>
              <StatsOverview events={events} />
              <TopSections events={events} />
              <DailyChart events={events} />
              <EventsList events={events} />
            </>
          )}
        </div>

        {/* Clear confirm modal */}
        {showClearConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full border border-[#E5E5E5]">
              <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4">
                <i className="ri-delete-bin-line text-red-500 text-xl" />
              </div>
              <h3 className="text-base font-black text-[#000000] mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Svuotare tutti i dati?
              </h3>
              <p className="text-sm text-[#A5A5A5] mb-5 leading-relaxed">
                Tutti gli eventi nel database verranno eliminati definitivamente. L&apos;operazione non è reversibile.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 py-2.5 rounded-xl border border-[#E5E5E5] text-sm font-bold text-[#555555] hover:bg-[#F6F6F6] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Annulla
                </button>
                <button
                  onClick={handleClear}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Elimina tutto
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </LoginGate>
  );
}
